import { useRef, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Toolbar,
  Typography,
} from "@mui/material";

import logoSuperApp from "../theme/images/logo-super-app.png";
import logoCsb from "../theme/images/logo-csb.png";
import logoFooter from "../theme/images/logo-footer.png";
import bgImg from "../theme/images/background-home.png";
import lapPhoneIcon from "../theme/images/heroframe.png";
import rescueSupportImg from "../theme/images/rescue-support-image.png";
import reportRescueImg from "../theme/images/report-rescue-image.png";
import googlePlayIcon from "../theme/images/google-play-icon.svg";
import appstoreIcon from "../theme/images/appstore-icon.svg";
import grayArrowIcon from "../theme/images/gray-arrow-icon.png";

const STORE_LINKS = {
  googlePlay:
    "https://play.google.com/store/apps/details?id=com.bglobal.csb.superapp&hl=vi",
  appStore:
    "https://apps.apple.com/us/app/bi%E1%BB%83n-vi%E1%BB%87t-nam/id6751949224",
};

const features = [
  {
    title: "Tiếp nhận phản ánh, cứu hộ",
    description:
      "Kết nối với người dân trên biển kịp thời, nắm được các hoạt động trên biển.",
    image: reportRescueImg,
  },
  {
    title: "Hỗ trợ cứu hộ, cứu nạn",
    description:
      "Hỗ trợ cứu hộ, cứu nạn trên biển nhanh chóng, kịp thời và hiệu quả.",
    image: rescueSupportImg,
  },
];

const PX = { xs: "16px", sm: "32px", md: "60px", lg: "80px" };

export default function LandingPage() {
  const [activeNav, setActiveNav] = useState("Trang chủ");
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const footerRef = useRef(null);

  const navItems = [
    { label: "Trang chủ", ref: heroRef },
    { label: "Tính năng chính", ref: featuresRef },
    { label: "Liên hệ", ref: footerRef },
  ];

  const handleNav = (item) => {
    setActiveNav(item.label);
    if (item.label === "Trang chủ") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (item.ref?.current) {
      item.ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Box sx={{ fontFamily: "'Geist', 'Inter', sans-serif", overflowX: "hidden" }}>
      {/* ════════ HEADER ════════ */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: "#fff",
          borderBottom: "1px solid #e5e7eb",
          zIndex: 100,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            px: PX,
            minHeight: { xs: "56px !important", md: "72px !important" },
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: { xs: 0.5, md: 1.5 },
            py: { xs: "6px", md: 0 },
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={logoCsb}
            alt="logo"
            sx={{
              width: { xs: 36, md: 52 },
              height: { xs: 36, md: 52 },
              objectFit: "contain",
              mr: { xs: 0.5, md: 1 },
            }}
          />
          {navItems.map((item) => {
            const isActive = activeNav === item.label;
            return (
              <Button
                key={item.label}
                onClick={() => handleNav(item)}
                disableElevation
                sx={{
                  borderRadius: "999px",
                  px: { xs: 1.5, md: 3 },
                  py: { xs: 0.5, md: 1 },
                  textTransform: "none",
                  fontSize: { xs: 13, md: 17 },
                  fontWeight: 600,
                  minWidth: 0,
                  backgroundColor: isActive ? "#1976d2" : "transparent",
                  color: isActive ? "#fff" : "#374151",
                  "&:hover": {
                    backgroundColor: isActive ? "#1565c0" : "#f3f4f6",
                  },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Toolbar>
      </AppBar>

      {/* ════════ HERO ════════ */}
      <Box
        ref={heroRef}
        sx={{
          position: "relative",
          width: "100%",
          minHeight: { xs: 560, sm: 640, md: "calc(100vh - 72px)" },
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${bgImg})`,
            backgroundSize: "cover",
            backgroundPosition: "center top",
            backgroundRepeat: "no-repeat",
            opacity: 0.2,
            zIndex: 0,
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            px: PX,
            py: { xs: 6, md: 0 },
            display: "flex",
            alignItems: "center",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 5, md: 4 },
          }}
        >
          {/* ── LEFT ── */}
          <Box sx={{ flex: "0 0 auto", width: { xs: "100%", md: "46%" } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: { xs: "14px", md: "24px" },
                mb: { xs: "20px", md: "32px" },
                flexWrap: "nowrap",
              }}
            >
              {/* Logo */}
              <Box
                component="img"
                src={logoSuperApp}
                alt="logo"
                sx={{
                  width: { xs: 72, sm: 90, md: 180 },
                  height: { xs: 72, sm: 90, md: 180 },
                  objectFit: "contain",
                  flexShrink: 0,
                  borderRadius: "20px",
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: 13, sm: 15, md: 22 },
                    color: "#475569",
                    fontWeight: 500,
                    lineHeight: 1.3,
                    mb: "6px",
                  }}
                >
                  Siêu ứng dụng
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Geist', 'Oswald', Arial, sans-serif",
                    fontSize: { xs: "1.6rem", sm: "2rem", md: "3rem" },
                    fontWeight: 900,
                    color: "#0d47a1",
                    lineHeight: 1.1,
                    mb: { xs: "12px", md: "24px" },
                    letterSpacing: "0.01em",
                  }}
                >
                  BIỂN VIỆT NAM
                </Typography>

                {/* Store badges */}
                <Box sx={{ display: "flex", gap: { xs: "10px", md: "16px" } }}>
                  <Box
                    component="a"
                    href={STORE_LINKS.googlePlay}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Box
                      component="img"
                      src={googlePlayIcon}
                      alt="Google Play"
                      sx={{ height: { xs: 32, sm: 38, md: 54 }, display: "block" }}
                    />
                  </Box>
                  <Box
                    component="a"
                    href={STORE_LINKS.appStore}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Box
                      component="img"
                      src={appstoreIcon}
                      alt="App Store"
                      sx={{ height: { xs: 32, sm: 38, md: 54 }, display: "block" }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Description */}
            <Typography
              sx={{
                fontSize: { xs: 16, sm: 20, md: 36 },
                color: "#1e293b",
                fontWeight: 400,
                lineHeight: 1.45,
                mb: "16px",
              }}
            >
              Ứng dụng đa chức năng cung cấp thông tin cho người dùng
            </Typography>

            {/* Bullets */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: "10px", md: "14px" } }}>
              {["An toàn & Thuận tiện", "Nhanh chóng & Kịp thời"].map(
                (text) => (
                  <Box
                    key={text}
                    sx={{ display: "flex", alignItems: "center", gap: "10px" }}
                  >
                    <Box
                      component="img"
                      src={grayArrowIcon}
                      alt=""
                      aria-hidden
                      sx={{
                        width: { xs: 20, md: 28 },
                        height: { xs: 20, md: 28 },
                        objectFit: "contain",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: { xs: 14, sm: 16, md: 20 }, color: "#475569" }}
                    >
                      {text}
                    </Typography>
                  </Box>
                ),
              )}
            </Box>
          </Box>

          {/* ── RIGHT: app preview ── */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={lapPhoneIcon}
              alt="App preview"
              sx={{
                width: "100%",
                maxWidth: { xs: 320, sm: 460, md: 700 },
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* ════════ TÍNH NĂNG CHÍNH ════════ */}
      <Box
        ref={featuresRef}
        sx={{ backgroundColor: "#f0f6ff", py: { xs: "48px", md: "72px" } }}
      >
        <Box sx={{ px: PX }}>
          <Box
            sx={{
              backgroundColor: "#fff",
              border: "1px solid rgba(0, 129, 219, 0.25)",
              padding: { xs: "24px 16px", sm: "36px 28px", md: "56px 72px" },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: { xs: "32px", md: "48px" },
            }}
          >
            {/* Heading */}
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: { xs: 26, sm: 34, md: 50 },
                  fontWeight: 800,
                  color: "#111827",
                  mb: "12px",
                }}
              >
                Tính năng chính
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 14, sm: 16, md: 25 },
                  fontWeight: 600,
                  color: "#6b7280",
                }}
              >
                Biển Việt Nam cung cấp các tính năng toàn diện phục vụ công tác quản lý và bảo vệ biển.
              </Typography>
            </Box>

            {/* 2 cards */}
            <Box
              sx={{
                display: "flex",
                gap: { xs: "20px", md: "36px" },
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "center",
                width: "100%",
              }}
            >
              {features.map((item) => (
                <Card
                  key={item.title}
                  sx={{
                    flex: { xs: "1 1 100%", md: "1 1 0" },
                    minWidth: 0,
                    borderRadius: "16px",
                    border: "1px solid rgba(0, 129, 219, 0.25)",
                    boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.25)",
                    overflow: "hidden",
                    transition: "transform .2s, box-shadow .2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0px 8px 32px 0px rgba(0, 0, 0, 0.25)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: { xs: 180, sm: 220, md: 300 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: { xs: "16px", md: "24px" },
                    }}
                  >
                    <Box
                      component="img"
                      src={item.image}
                      alt={item.title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>

                  <CardContent
                    sx={{
                      px: { xs: "20px", md: "36px" },
                      pb: "32px !important",
                      pt: "8px",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: 16, sm: 19, md: 28 },
                        color: "#111827",
                        mb: "10px",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: 13, sm: 15, md: 20 },
                        color: "#6b7280",
                        lineHeight: 1.7,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ════════ FOOTER ════════ */}
      <Box
        ref={footerRef}
        sx={{ backgroundColor: "#fff", borderTop: "1px solid #e5e7eb", overflow: "hidden" }}
      >
        <Box sx={{ px: PX }}>
          {/* Main row */}
          <Box
            sx={{
              pt: { xs: "40px", md: "60px" },
              pb: { xs: "32px", md: "44px" },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: "36px", md: 0 },
            }}
          >
            {/* LEFT */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "28px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "10px", md: "16px" } }}>
                <Box
                  component="img"
                  src={logoFooter}
                  alt="logo"
                  sx={{
                    width: { xs: 56, md: 100 },
                    height: { xs: 56, md: 100 },
                    objectFit: "contain",
                  }}
                />
                <Typography
                  sx={{ fontWeight: 700, fontSize: { xs: 24, sm: 32, md: 44 }, color: "#1565c0" }}
                >
                  Biển Việt Nam
                </Typography>
              </Box>

              {/* 1 box bọc 2 store icon */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: { xs: "10px", md: "16px" },
                  px: { xs: "14px", md: "20px" },
                  py: { xs: "14px", md: "20px" },
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                  width: "fit-content",
                }}
              >
                <Box
                  component="a"
                  href={STORE_LINKS.googlePlay}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: "inline-flex" }}
                >
                  <Box
                    component="img"
                    src={googlePlayIcon}
                    alt="Google Play"
                    sx={{ height: { xs: 36, md: 56 } }}
                  />
                </Box>
                <Box
                  component="a"
                  href={STORE_LINKS.appStore}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: "inline-flex" }}
                >
                  <Box
                    component="img"
                    src={appstoreIcon}
                    alt="App Store"
                    sx={{ height: { xs: 36, md: 56 } }}
                  />
                </Box>
              </Box>
            </Box>

            {/* RIGHT */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                maxWidth: { xs: "100%", md: 700 },
              }}
            >
              <Typography sx={{ fontSize: { xs: 14, md: 18 }, color: "#9ca3af" }}>
                Một ứng dụng được phát triển bởi
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 20, sm: 24, md: 32 },
                  color: "#6b7280",
                }}
              >
                Công ty cổ phần bGlobal
              </Typography>
              <Typography
                sx={{ fontSize: { xs: 13, md: 18 }, color: "#6b7280", lineHeight: 1.6 }}
              >
                Phòng 702, Tầng 7, Số 2 ngõ 219 Trung Kính, Phường Yên Hòa, Quận
                Cầu Giấy, Thành phố Hà Nội, Việt Nam .
              </Typography>
              <Typography sx={{ fontSize: { xs: 13, md: 18 }, color: "#6b7280" }}>
                Email: Contact@bglobalcorp.com
              </Typography>
              <Typography sx={{ fontSize: { xs: 13, md: 18 }, color: "#6b7280" }}>
                SĐT: +84 (0) 866596247
              </Typography>
            </Box>
          </Box>

          {/* Copyright — không có borderTop */}
          <Box
            sx={{
              py: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            <Typography sx={{ fontSize: { xs: 12, md: 18 }, color: "#9ca3af" }}>
              Copyright © bGlobal JSC. All Rights Reserved.
            </Typography>
            <Typography sx={{ fontSize: { xs: 12, md: 18 }, color: "#9ca3af" }}>
              Giấy chứng nhận ĐKKD số 0108772805 do SKHĐT Hà Nội cấp ngày
              07/06/2019.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
